import React from "react";
import { Heading, Subheading } from "../../design/heading";
import { Table, TableBody } from "../../design/table";
import { useFinancialTransaction } from "../../contexts/FinancialTransactionContext";
import EmptyStateView from "../../components/EmptyStateView";
import FinancialTransactionListItem from "./components/FinancialTransactionListItem";
import ErrorView from "../../components/ErrorView";
import LoadingView from "../../components/LoadingView";
import { useMatter } from "../../contexts/MatterContext";
import NoMatterView from "../../components/NoMatterView";
import moment from "moment";
import RefreshButton from "./components/RefreshButton";
import PlaidButton from "../../components/PlaidButton";

const FinancialTransactionListPage: React.FC = () => {
  const { currentMatterId } = useMatter();
  const { financialTransactions, loading, error } = useFinancialTransaction();

  if (!currentMatterId) return <NoMatterView />;
  if (loading.list)
    return <LoadingView title="Loading Financial Transactions..." />;
  if (error.list) return <ErrorView error={error.list} />;

  if (financialTransactions.length === 0) {
    return (
      <EmptyStateView
        title="No Transactions"
        message="No transactions found. Add one to get started."
        action={<PlaidButton>Download Transactions</PlaidButton>}
      />
    );
  }

  // Group transactions by date (YYYY-MM-DD)
  const groupedByDate = financialTransactions.reduce((acc: any, tx) => {
    const dateKey = moment(tx.date).format("YYYY-MM-DD");
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(tx);
    return acc;
  }, {});

  // Sort by newest day first
  const sortedDates = Object.keys(groupedByDate).sort((a, b) =>
    moment(b).diff(moment(a)),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 mb-6">
        <Heading className="text-2xl font-bold">Financial Transactions</Heading>
        <RefreshButton type="transactions" />
      </div>

      {sortedDates.map((dateKey) => (
        <div key={dateKey}>
          <div className="p-2 bg-gray-100 dark:bg-gray-800">
            <Subheading>{moment(dateKey).format("MMMM D, YYYY")}</Subheading>
          </div>

          <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
            <TableBody>
              {groupedByDate[dateKey].map((tx: any) => (
                <FinancialTransactionListItem
                  key={tx._id}
                  financialTransaction={tx}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      ))}
    </div>
  );
};

export default FinancialTransactionListPage;
